import type { Feature } from "@/types/feature";

// Single source of truth for which features have synthetic preview-mode
// data wired up. Drives both the banner's route gating (PreviewBanner)
// and the feature splash bypass (useFeatureAccess). Adding a new
// preview-mode feature here is the only edit needed for both consumers
// to pick it up.
type PreviewModeFeature = {
  pathPrefix: string;
  // Set when the preview-mode feature corresponds to a Feature gated by
  // useFeatureAccess. Omit for preview-mode features outside that flow
  // (e.g. routes not represented in the Feature enum).
  feature?: Feature;
};

export const PREVIEW_MODE_FEATURES: readonly PreviewModeFeature[] = [
  { pathPrefix: "/web-analytics", feature: "web_analytics" },
  { pathPrefix: "/funnels", feature: "funnels" },
  { pathPrefix: "/surveys", feature: "surveys" },
  { pathPrefix: "/ai-chat", feature: "ai_chat" },
  // Session Replay gates on the shared sessionReplayAndHeatmaps entitlement via
  // SessionReplayRouteWrapper rather than useFeatureAccess, so it has no
  // Feature entry here — the path prefix alone drives the PreviewBanner.
  { pathPrefix: "/session-replay" },
  // Heatmaps is the same shape: HeatmapsPage gates on the per-account
  // `heatmapsEnabled` flag directly, so no Feature entry.
  { pathPrefix: "/heatmaps" },
  { pathPrefix: "/attribution", feature: "attribution" },
  // Ad Performance / legacy CRO. Gates on the `marketingPerformanceAnalysis`
  // LaunchDarkly-style flag via useFeatureFlags(), not useFeatureAccess, so
  // (like Session Replay and Heatmaps above) it has no Feature entry — the
  // path prefix alone drives the PreviewBanner. The masquerade/demo account
  // used for preview mode still needs that flag enabled server-side; this
  // ticket is frontend-only and doesn't change flag state.
  { pathPrefix: "/marketing-performance-analysis" },
];

export const PREVIEW_MODE_PATH_PREFIXES: readonly string[] =
  PREVIEW_MODE_FEATURES.map((f) => f.pathPrefix);

export const PREVIEW_MODE_GATED_FEATURES: ReadonlySet<Feature> = new Set(
  PREVIEW_MODE_FEATURES.map((f) => f.feature).filter(
    (f): f is Feature => f !== undefined,
  ),
);

export function pathHasPreviewData(pathname: string): boolean {
  return PREVIEW_MODE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}
